import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmailEditor } from '@/components/email/EmailEditor';
import {
  Plus, Search, Star, Pencil, Trash2, LayoutTemplate, Eye, Copy,
} from 'lucide-react';
import { BUILTIN_TEMPLATES, CATEGORY_LABELS } from '@/lib/emailTemplates';
import { emailTemplatesAPI } from '@/lib/api';
import { toast } from 'sonner';

// ── HTML preview in iframe ─────────────────────────────────────────────────────

const TemplatePreview = ({ html, scale = 0.45, height = 280 }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <style>body{margin:0;padding:0;background:#fff;}</style>
      </head><body>${html}</body></html>`);
    doc.close();
  }, [html]);

  return (
    <div style={{ width: '100%', height, overflow: 'hidden', background: '#FBF8F2', border: '1px solid #E3DDD1', borderRadius: 8 }}>
      <iframe
        ref={iframeRef}
        title="preview"
        style={{
          width: '600px',
          height: `${height / scale}px`,
          border: 'none',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

// ── Template card ──────────────────────────────────────────────────────────────

const TemplateCard = ({ template, onPreview, onEdit, onDuplicate, onDelete }) => (
  <div className="bg-white border border-[#E3DDD1] rounded-xl overflow-hidden hover:shadow-md transition-shadow group">
    {/* Preview thumbnail */}
    <div className="relative cursor-pointer" onClick={() => onPreview(template)} style={{ height: 200 }}>
      <TemplatePreview html={template.html_body} scale={0.33} height={200} />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="bg-white rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-md">
          <Eye className="w-3.5 h-3.5 text-[#1E2A33]" />
          <span className="text-xs font-medium text-[#1E2A33]">Preview</span>
        </div>
      </div>
    </div>

    {/* Info */}
    <div className="p-4">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {template.is_builtin && <Star className="w-3.5 h-3.5 text-[#A62B1F] flex-shrink-0" />}
          <p className="font-semibold text-[#1E2A33] text-sm truncate">{template.name}</p>
        </div>
        <Badge
          variant="secondary"
          className="ml-2 flex-shrink-0 text-[10px] py-0 px-1.5"
          style={{ backgroundColor: template.is_builtin ? '#F7ECEA' : '#F3EFE7', color: template.is_builtin ? '#A62B1F' : '#5C6670' }}
        >
          {CATEGORY_LABELS[template.category] || template.category}
        </Badge>
      </div>
      {template.description && (
        <p className="text-xs text-[#5C6670] line-clamp-2 mb-3">{template.description}</p>
      )}

      <div className="flex gap-2 mt-3">
        {template.is_builtin ? (
          <>
            <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => onDuplicate(template)}>
              <Copy className="w-3 h-3 mr-1" /> Duplicate & Edit
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => onEdit(template)}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="ghost" className="text-xs text-[#5C6670] hover:text-red-500 px-2" onClick={() => onDelete(template)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  </div>
);

// ── Main page ──────────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', description: '', category: 'custom', html_body: '' };

export default function EmailTemplates() {
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Editor sheet
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Preview dialog
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const res = await emailTemplatesAPI.list();
      setUserTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allTemplates = [...BUILTIN_TEMPLATES, ...userTemplates];
  const categories = ['all', ...new Set(allTemplates.map(t => t.category))];

  const filtered = allTemplates.filter(t => {
    const matchCat = activeCategory === 'all' || t.category === activeCategory;
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const openNew = () => {
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
    setShowEditor(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setForm({ name: t.name, description: t.description || '', category: t.category, html_body: t.html_body });
    setShowEditor(true);
  };

  const openDuplicate = (t) => {
    setEditingTemplate(null);
    setForm({ name: `${t.name} (Copy)`, description: t.description || '', category: t.category, html_body: t.html_body });
    setShowEditor(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      if (editingTemplate) {
        await emailTemplatesAPI.update(editingTemplate._id, form);
        toast.success('Template updated');
      } else {
        await emailTemplatesAPI.create(form);
        toast.success('Template saved');
      }
      setShowEditor(false);
      await load();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await emailTemplatesAPI.delete(deleteTarget._id);
      toast.success('Template deleted');
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout title="Email Templates">
      {/* Top bar */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6670]" />
          <Input
            placeholder="Search templates..."
            className="pl-9 text-sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Category filter */}
        <div className="flex gap-1.5">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === cat
                  ? 'bg-[#A62B1F] text-white border-[#A62B1F]'
                  : 'bg-white text-[#5C6670] border-[#E3DDD1] hover:border-[#A62B1F]'
              }`}
            >
              {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>

        <Button
          style={{ backgroundColor: '#A62B1F', color: 'white' }}
          className="ml-auto text-sm"
          onClick={openNew}
        >
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#5C6670]">Loading templates…</p>
        </div>
      ) : (
        <>
          {/* Built-in section */}
          {(activeCategory === 'all' || BUILTIN_TEMPLATES.some(t => t.category === activeCategory)) && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-4 h-4 text-[#A62B1F]" />
                <h2 className="font-semibold text-[#1E2A33] text-sm" style={{ fontFamily: 'Manrope' }}>Starter Templates</h2>
                <span className="text-xs text-[#9AA3AB]">— duplicate to customise</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {BUILTIN_TEMPLATES
                  .filter(t => activeCategory === 'all' || t.category === activeCategory)
                  .filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()))
                  .map(t => (
                    <TemplateCard
                      key={t._id}
                      template={t}
                      onPreview={setPreviewTemplate}
                      onEdit={openEdit}
                      onDuplicate={openDuplicate}
                      onDelete={setDeleteTarget}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Saved templates */}
          {userTemplates.filter(t => activeCategory === 'all' || t.category === activeCategory).length > 0 && (
            <div>
              <h2 className="font-semibold text-[#1E2A33] text-sm mb-4" style={{ fontFamily: 'Manrope' }}>Your Templates</h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {userTemplates
                  .filter(t => activeCategory === 'all' || t.category === activeCategory)
                  .filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()))
                  .map(t => (
                    <TemplateCard
                      key={t._id}
                      template={t}
                      onPreview={setPreviewTemplate}
                      onEdit={openEdit}
                      onDuplicate={openDuplicate}
                      onDelete={setDeleteTarget}
                    />
                  ))}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <LayoutTemplate className="w-12 h-12 text-[#E7C3BC] mb-3" />
              <p className="text-[#5C6670] font-medium">No templates found</p>
              <p className="text-xs text-[#9AA3AB] mt-1">Create a new template or clear your search</p>
            </div>
          )}
        </>
      )}

      {/* ── Editor sheet ─────────────────────────────────────────────────────── */}
      <Sheet open={showEditor} onOpenChange={setShowEditor}>
        <SheetContent side="right" className="w-full max-w-4xl p-0 flex flex-col" style={{ maxWidth: '900px' }}>
          <SheetHeader className="px-6 py-4 border-b border-[#E3DDD1] flex-shrink-0">
            <SheetTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              {editingTemplate ? 'Edit Template' : 'New Template'}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1E2A33] mb-1 block">Template Name</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Hotel Outreach — Header CTA"
                />
              </div>
              <div>
                <Label className="text-[#1E2A33] mb-1 block">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#1E2A33] mb-1 block">Description <span className="text-[#9AA3AB] font-normal">(optional)</span></Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of when to use this template"
              />
            </div>

            <div>
              <Label className="text-[#1E2A33] mb-2 block">Email Body</Label>
              <EmailEditor
                value={form.html_body}
                onChange={html => setForm(f => ({ ...f, html_body: html }))}
                placeholder="Design your template here. Use {{first_name}}, {{company}}, {{assigned_to_name}} as variables."
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[#E3DDD1] flex justify-end gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button
              style={{ backgroundColor: '#A62B1F', color: 'white' }}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : editingTemplate ? 'Save Changes' : 'Save Template'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Preview dialog ────────────────────────────────────────────────────── */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b border-[#E3DDD1]">
            <DialogTitle className="text-[#1E2A33]">{previewTemplate?.name}</DialogTitle>
            <DialogDescription className="text-[#5C6670]">{previewTemplate?.description}</DialogDescription>
          </DialogHeader>
          <div className="p-6 overflow-auto" style={{ maxHeight: '70vh' }}>
            {previewTemplate && (
              <TemplatePreview html={previewTemplate.html_body} scale={0.8} height={600} />
            )}
          </div>
          <div className="px-6 py-3 border-t border-[#E3DDD1] flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(null)}>Close</Button>
            {previewTemplate?.is_builtin ? (
              <Button size="sm" variant="outline" onClick={() => { openDuplicate(previewTemplate); setPreviewTemplate(null); }}>
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate & Edit
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => { openEdit(previewTemplate); setPreviewTemplate(null); }}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm ────────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1E2A33]">Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
